/**
 * The studio's calendar day, and nothing else.
 *
 * This module has NO imports on purpose. compDayVisible.ts used to own
 * studioDayKey, but that file also pulls in helperCompDay.ts (React hooks,
 * Firebase app and auth) and countdownEvents.ts, so a page that only wanted
 * "what day is it at the studio" dragged the whole comp-day machinery behind
 * it. The practice-staff harness proved the cost on PR #1030: its Firebase
 * mock exports db alone, so the page failed to link and never rendered.
 * Anything that needs the studio's day imports THIS file; compDayVisible.ts
 * re-exports both names so its existing importers keep working unchanged.
 */

/** The studio is in Missouri. Every comp date string is a day in this zone. */
export const STUDIO_TIME_ZONE = 'America/Chicago';

/**
 * 'YYYY-MM-DD' for an instant, in the studio's zone.
 *
 * en-CA gives ISO order from toLocaleDateString, which is the trick the rest of
 * the Hub's date keys use. A runtime with no Intl time zone data falls back to
 * the device's own day rather than throwing: a ticker is not worth a crash, and
 * being wrong by one zone is strictly better than a blank page.
 */
export function studioDayKey(nowMs: number): string {
  const when = new Date(nowMs);
  try {
    return when.toLocaleDateString('en-CA', { timeZone: STUDIO_TIME_ZONE });
  } catch {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`;
  }
}

/**
 * The clock time of an instant at the studio, like "6:46 AM".
 *
 * EVERY TIME SHOWN TO ANYONE AT THIS STUDIO IS STUDIO TIME (Bobbie,
 * 2026-09-01). The container this app is served from runs UTC and a phone runs
 * wherever its owner is standing, so a bare toLocaleTimeString would put a
 * different hour in front of Kim than in front of Bobbie for the same instant.
 * This asks for America/Chicago by name, the same way studioDayKey above asks
 * for the day.
 *
 * A runtime with no Intl time zone data falls back to the device's own clock
 * rather than throwing, for the same reason the day key does: being an hour
 * out is strictly better than a blank page.
 */
export function studioClockLabel(atMs: number): string {
  const when = new Date(atMs);
  if (Number.isNaN(when.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: STUDIO_TIME_ZONE,
      hour: 'numeric',
      minute: '2-digit',
    }).format(when);
  } catch {
    return when.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
}

/**
 * When something happened, at the studio, said the shortest honest way.
 *
 * "6:46 AM" while it is still the same studio day, "Sep 16 at 6:46 AM" once it
 * is not. A tab left open overnight used to read "Scanned at 8:15 PM" the next
 * morning, which is a true time attached to the wrong day and reads as a list
 * taken fifteen minutes ago. The day appears exactly when it is needed and
 * never clutters the ordinary case.
 *
 * Both instants are compared through studioDayKey above, so "today" means the
 * studio's day in America/Chicago, not the viewer's.
 */
export function studioStampLabel(atMs: number, nowMs: number = Date.now()): string {
  const when = new Date(atMs);
  if (Number.isNaN(when.getTime())) return '';
  const time = studioClockLabel(atMs);
  if (studioDayKey(atMs) === studioDayKey(nowMs)) return time;
  try {
    const day = new Intl.DateTimeFormat('en-US', {
      timeZone: STUDIO_TIME_ZONE,
      month: 'short',
      day: 'numeric',
    }).format(when);
    return `${day} at ${time}`;
  } catch {
    return time;
  }
}
